import { useAppContext } from '@faro/app-context';
import {
    CommandResponse,
    ObjectivesAndEndpointsSection,
    PlacementPayload,
    RevisionedCommandResponse,
    StudyEntityType,
} from '@faro/study-designer-model';
import { createSectionDuplicateMutation, SectionDuplicateHook } from '../createSectionDuplicateMutation';
import { createSectionInsertMutation, SectionInsertHook } from '../createSectionInsertMutation';
import {
    useObjectivesAndEndpointsSectionDelete,
    useObjectivesAndEndpointsSectionMove,
    useObjectivesAndEndpointsSectionUpdate,
} from './ObjectivesAndEndpointsSectionHooks';

export type ObjectivesAndEndpointsSectionInsertHook = SectionInsertHook<ObjectivesAndEndpointsSection>;

/**
 * Mutation hook to insert an objectives and endpoints section.
 * @returns A function that inserts an objectives and endpoints section.
 */
export const useObjectivesAndEndpointsSectionInsertMutation: (
    studyId: string
) => ObjectivesAndEndpointsSectionInsertHook = createSectionInsertMutation<ObjectivesAndEndpointsSection>(
    StudyEntityType.ObjectivesAndEndpointsSection,
    'objectives-and-endpoints-section',
    'Failed to create objectives and endpoints section'
);

export type ObjectivesAndEndpointsSectionUpdateHook = (
    objectivesAndEndpointsSection: ObjectivesAndEndpointsSection
) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to update an objectives and endpoints section.
 * @returns A function that updates an objectives and endpoints section.
 */
export function useObjectivesAndEndpointsSectionUpdateMutation(
    studyId: string
): ObjectivesAndEndpointsSectionUpdateHook {
    const { showErrorHandler } = useAppContext();

    const updateObjectivesAndEndpointsSection = useObjectivesAndEndpointsSectionUpdate({ studyId });

    function update(objectivesAndEndpointsSection: ObjectivesAndEndpointsSection) {
        return updateObjectivesAndEndpointsSection
            .mutateAsync(objectivesAndEndpointsSection)
            .catch(showErrorHandler('Failed to update objectives and endpoints section'));
    }

    return update;
}

export type ObjectivesAndEndpointsSectionDeleteHook = (objectivesAndEndpointsSection: {
    id: string;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to delete an objectives and endpoints section.
 * @returns A function that deletes an objectives and endpoints section.
 */
export function useObjectivesAndEndpointsSectionDeleteMutation(
    studyId: string
): ObjectivesAndEndpointsSectionDeleteHook {
    const { showErrorHandler } = useAppContext();

    const deleteObjectivesAndEndpointsSection = useObjectivesAndEndpointsSectionDelete({ studyId });

    function deleteObjectivesAndEndpoints(objectivesAndEndpointsSection: { id: string }) {
        return deleteObjectivesAndEndpointsSection
            .mutateAsync({ id: objectivesAndEndpointsSection.id, studyId })
            .catch(showErrorHandler('Failed to delete objectives and endpoints section'));
    }

    return deleteObjectivesAndEndpoints;
}

export type ObjectivesAndEndpointsSectionDuplicateHook = SectionDuplicateHook;

/**
 * Mutation hook to duplicate an objectives and endpoints section.
 * @returns A function that duplicates an objectives and endpoints section.
 */
export const useObjectivesAndEndpointsSectionDuplicateMutation: (
    studyId: string
) => ObjectivesAndEndpointsSectionDuplicateHook = createSectionDuplicateMutation(
    StudyEntityType.ObjectivesAndEndpointsSection,
    'objectives-and-endpoints-section',
    'Failed to duplicate objectives and endpoints section'
);

export type ObjectivesAndEndpointsSectionMoveHook = (objectivesAndEndpointsSection: {
    id: string;
    placement: PlacementPayload;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to move an objectives and endpoints section.
 * @returns A function that moves an objectives and endpoints section.
 */
export function useObjectivesAndEndpointsSectionMoveMutation(studyId: string): ObjectivesAndEndpointsSectionMoveHook {
    const { showErrorHandler } = useAppContext();

    const moveObjectivesAndEndpointsSection = useObjectivesAndEndpointsSectionMove({ studyId });

    function move(objectivesAndEndpointsSection: { id: string; placement: PlacementPayload }) {
        return moveObjectivesAndEndpointsSection
            .mutateAsync({ ...objectivesAndEndpointsSection, studyId })
            .catch(showErrorHandler('Failed to move objectives and endpoints section'));
    }

    return move;
}
